import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  textBlockHasValue, 
  getBlockPlacementEntries, 
  supportsMultiplePlacements 
} from '../../components/documentSigning/utils';
import { DEFAULT_SIGNATURE_WIDTH_PCT } from '../../components/documentSigning/constants';

const convertPctToPdfCoords = (xPct, yPct, pageNumber, pdfPages) => {
  const pdfPage = pdfPages[pageNumber - 1];
  if (!pdfPage) return { pdfX: null, pdfY: null };

  const pdfX = xPct * pdfPage.width;
  const pdfY = yPct * pdfPage.height;

  return { pdfX, pdfY };
};

export const useTextBlockPlacement = (pdfPages, isPreviewMode, defaultApiResponse = null) => {
  const [textBlocks, setTextBlocks] = useState([]);
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [defaultBlockPlacements, setDefaultBlockPlacements] = useState({});
  const [isSignatureRequired, setIsSignatureRequired] = useState(false);

  const initializeFromApiResponse = useCallback((apiResponse, pdfPages) => {
    if (!apiResponse || !pdfPages || pdfPages.length === 0) {
      setIsLoadingFields(false);
      return;
    }

    if (isPreviewMode) {
      setIsLoadingFields(false);
      return;
    }

    const textPlacements = apiResponse.defaultTextPlacements ||
                          (apiResponse.defaultPlacements?.textBlocks) ||
                          [];

    const defaultPlacements = {};

    if (textPlacements.length > 0) {
      const newTextBlocks = textPlacements.map((blockDef) => {
        const placements = (blockDef.placements || []).map((placementDef) => {
          const { pdfX, pdfY } = convertPctToPdfCoords(
            placementDef.xPct,
            placementDef.yPct,
            placementDef.pageNumber,
            pdfPages
          );

          if (pdfX === null || pdfY === null) {
            return null;
          }

          const pdfPage = pdfPages[placementDef.pageNumber - 1];
          const widthPct = placementDef.widthPct || blockDef.widthPct || 0.25;
          const textWidth = pdfPage ? pdfPage.width * widthPct : null;
          const fontSize = 12;

          // For text, xPct and yPct from API represent the starting point (left-aligned)
          // Don't adjust canvasXPct - use xPct directly as the starting position
          // yPct from API is inverted (from bottom), so convert to top-based for canvas rendering
          const canvasXPct = placementDef.xPct;
          const canvasYPct = 1 - placementDef.yPct;

          const placement = {
            pageNumber: placementDef.pageNumber,
            xPct: placementDef.xPct, // Starting point in wrapper coordinates
            yPct: canvasYPct, // Starting point in wrapper coordinates (converted to top-based)
            canvasXPct, // Starting point in canvas coordinates
            canvasYPct, // Starting point in canvas coordinates (top-based)
            widthPct,
            pdfX, // Starting point for PDF rendering
            pdfY, // Starting point for PDF rendering
            pdfWidth: textWidth,
            pdfHeight: fontSize,
          };

          return {
            placement,
            value: null,
          };
        }).filter(Boolean);

        const blockId = blockDef.id || blockDef.blockId;

        if (placements.length > 0) {
          defaultPlacements[blockId] = placements;
        }

        // Automatically enable multiple placements if:
        // 1. Block has multiple placements in API response
        // 2. Block type is "initial" (initials typically need multiple placements)
        const hasMultiplePlacements = placements.length > 1;
        const isInitialType = (blockDef.type || 'text') === 'initial';
        const shouldAllowMultiple = blockDef.allowMultiplePlacements === true || hasMultiplePlacements || isInitialType;

        return {
          id: blockId,
          blockId: blockDef.blockId || blockDef.id,
          label: blockDef.label,
          type: blockDef.type || 'text',
          placeholder: blockDef.placeholder || '',
          required: blockDef.required !== false,
          widthPct: blockDef.widthPct || 0.25,
          value: blockDef.type === 'date' ? null : '',
          placement: null,
          placements: [],
          allowMultiplePlacements: shouldAllowMultiple,
          captureValuePerPlacement: blockDef.captureValuePerPlacement === true,
          totalDefaultPlacements: placements.length, // Track how many default placements are available
        };
      });

      setTextBlocks(newTextBlocks);
      setDefaultBlockPlacements(defaultPlacements);
    } else {
      setTextBlocks([]);
      setDefaultBlockPlacements({});
    }

    const sigPlacement = apiResponse.defaultSignaturePlacement ||
                        apiResponse.defaultPlacements?.signature;

    setIsSignatureRequired(Boolean(sigPlacement));

    if (sigPlacement) {
      const { pdfX, pdfY } = convertPctToPdfCoords(
        sigPlacement.xPct,
        sigPlacement.yPct,
        sigPlacement.pageNumber,
        pdfPages
      );

      if (pdfX !== null && pdfY !== null) {
        const pdfPage = pdfPages[sigPlacement.pageNumber - 1];
        const widthPct = sigPlacement.widthPct || DEFAULT_SIGNATURE_WIDTH_PCT;
        const sigWidth = pdfPage ? pdfPage.width * widthPct : null;
        const sigHeight = pdfPage ? (pdfPage.width * widthPct * 0.086) : null;

        const adjustedPdfX = pdfX;
        const adjustedPdfY = pdfY - sigHeight;

        const topBasedYPct = 1 - sigPlacement.yPct;
        const canvasXPct = sigPlacement.xPct;
        const canvasYPct = topBasedYPct;

        const signaturePlacement = {
          pageNumber: sigPlacement.pageNumber,
          xPct: sigPlacement.xPct,
          yPct: topBasedYPct,
          canvasXPct,
          canvasYPct,
          widthPct,
          pdfX: adjustedPdfX,
          pdfY: adjustedPdfY,
          pdfWidth: sigWidth,
          pdfHeight: sigHeight,
        };

        setDefaultBlockPlacements(prev => ({
          ...prev,
          signature: signaturePlacement
        }));
      }
    }

    setIsLoadingFields(false);
  }, [isPreviewMode]);

  useEffect(() => {
    if (defaultApiResponse && pdfPages.length > 0 && !isPreviewMode) {
      initializeFromApiResponse(defaultApiResponse, pdfPages);
    }
  }, [defaultApiResponse, pdfPages, isPreviewMode, initializeFromApiResponse]);

  const signatureDefaultPlacement = useMemo(() => {
    return defaultBlockPlacements.signature || null;
  }, [defaultBlockPlacements]);

  useEffect(() => {
    if (!defaultApiResponse) {
      setIsLoadingFields(true);
      setTextBlocks([]);
      setDefaultBlockPlacements({});
      setIsSignatureRequired(false);
    }
  }, [defaultApiResponse]);

  const handleTextBlockValueChange = useCallback((blockId, value) => {
    setTextBlocks((prev) =>
      prev.map((block) =>
        block.id === blockId
          ? {
              ...block,
              value:
                block.type === 'date'
                  ? value
                  : typeof value === 'string'
                    ? value
                    : value != null
                      ? String(value)
                      : '',
            }
          : block
      )
    );
  }, []);

  const handleResetTextPlacement = useCallback((blockId) => {
    setTextBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== blockId) return block;
        const existingEntries = getBlockPlacementEntries(block);
        if (existingEntries.length <= 1) {
          return { ...block, placement: null, placements: [] };
        }
        const updatedEntries = existingEntries.slice(0, -1);
        return {
          ...block,
          placement: updatedEntries[0]?.placement || null,
          placements: updatedEntries,
        };
      })
    );
  }, []);

  const queuePlacementForBlock = useCallback((block, valueOverride, showToast, scrollToPlacement, scrollToBottom) => {
    if (!block) return false;

    const hasDefaultPlacement = defaultBlockPlacements[block.id] && defaultBlockPlacements[block.id].length > 0;

    if (block.captureValuePerPlacement) {
      const value = valueOverride ?? block.value;
      if (!value) {
        showToast(`Select ${block.label} before placing.`, 'error');
        return false;
      }

      if (hasDefaultPlacement) {
        const existingEntries = getBlockPlacementEntries(block);
        const totalDefaultPlacements = defaultBlockPlacements[block.id].length;
        
        // Check if all default placements have been used
        if (existingEntries.length >= totalDefaultPlacements) {
          showToast(`All ${block.label} placements have been added.`, 'info');
          return false;
        }

        const defaultPlacementEntry = defaultBlockPlacements[block.id][existingEntries.length] || defaultBlockPlacements[block.id][0];

        if (defaultPlacementEntry && defaultPlacementEntry.placement) {
          // For single placement: scroll only if not already on that page
          // For multiple placements: always scroll to show each placement location
          if (scrollToPlacement) {
            scrollToPlacement(defaultPlacementEntry.placement, totalDefaultPlacements > 1);
          }

          const nextEntry = {
            placement: defaultPlacementEntry.placement,
            value
          };
          const supportsMultiple = Boolean(
            block.allowMultiplePlacements || supportsMultiplePlacements(block)
          );
          const nextEntries = supportsMultiple ? [...existingEntries, nextEntry] : [nextEntry];

          setTextBlocks((prev) =>
            prev.map((b) => {
              if (b.id !== block.id) return b;
              return {
                ...b,
                placement: nextEntries[0]?.placement || null,
                placements: nextEntries,
              };
            })
          );

          const remaining = totalDefaultPlacements - nextEntries.length;
          showToast(
            supportsMultiple && remaining > 0
              ? `${block.label} placement added (${remaining} remaining).`
              : supportsMultiple
                ? `${block.label} placement added.`
                : `${block.label} placement saved.`,
            'success'
          );
          return true;
        }
      } else {
        // No default placement - scroll to bottom for manual placement
        if (scrollToBottom) {
          scrollToBottom();
        }
      }

      return true;
    }

    if (!textBlockHasValue(block)) {
      showToast(`Please enter ${block.label}.`, 'error');
      return false;
    }

    if (hasDefaultPlacement) {
      const existingEntries = getBlockPlacementEntries(block);
      const totalDefaultPlacements = defaultBlockPlacements[block.id].length;
      
      // Check if all default placements have been used
      if (existingEntries.length >= totalDefaultPlacements) {
        showToast(`All ${block.label} placements have been added.`, 'info');
        return false;
      }

      const defaultPlacementEntry = defaultBlockPlacements[block.id][existingEntries.length] || defaultBlockPlacements[block.id][0];

      if (defaultPlacementEntry && defaultPlacementEntry.placement) {
        // For single placement: scroll only if not already on that page
        // For multiple placements: always scroll to show each placement location
        if (scrollToPlacement) {
          scrollToPlacement(defaultPlacementEntry.placement, totalDefaultPlacements > 1);
        }

        const resolvedValue = existingEntries[0]?.value ?? block.value ?? null;
        const nextEntry = {
          placement: defaultPlacementEntry.placement,
          value: resolvedValue
        };
        const supportsMultiple = Boolean(
          block.allowMultiplePlacements || supportsMultiplePlacements(block)
        );
        const nextEntries = supportsMultiple ? [...existingEntries, nextEntry] : [nextEntry];

        setTextBlocks((prev) =>
          prev.map((b) => {
            if (b.id !== block.id) return b;
            return {
              ...b,
              placement: nextEntries[0]?.placement || null,
              placements: nextEntries,
            };
          })
        );

        const remaining = totalDefaultPlacements - nextEntries.length;
        showToast(
          supportsMultiple && remaining > 0
            ? `${block.label} placement added (${remaining} remaining).`
            : supportsMultiple
              ? `${block.label} placement added.`
              : `${block.label} placement saved.`,
          'success'
        );
        return true;
      }
    } else {
      // No default placement - scroll to bottom for manual placement
      if (scrollToBottom) {
        scrollToBottom();
      }
    }

    return true;
  }, [defaultBlockPlacements]);

  return {
    textBlocks,
    isLoadingFields,
    defaultBlockPlacements,
    signatureDefaultPlacement,
    isSignatureRequired,
    handleTextBlockValueChange,
    handleResetTextPlacement,
    queuePlacementForBlock,
    setTextBlocks,
  };
};
